import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Alert from 'src/components/atoms/alert'
import Form, { FormItem } from 'src/components/forms'
import { removeFromStorage } from 'src/shared/helpers/sessionStorageHelpers'
import { UserActions, useUserDispatch } from 'src/state/userState'

import Button from '../../../../components/atoms/button'
import QRCode from '../../../../components/atoms/QRCode'
import VerificationCodeInput from '../../../../components/molecules/verificationCodeInput'
import { MFAResponse } from '../../../../hooks/useLogin/types'
import { INITIAL_VERIFICATION_CODE, VERIFICATION_CODE_DIGITS } from './constants'

import { Typography } from 'antd'

const { Title, Paragraph } = Typography

interface Props extends Omit<MFAResponse, 'partialAuthToken'> {
    error: boolean
    onSubmit: (verificationCode: string) => void
}

const MFAForm = (props: Props) => {
    const { uri, secretKey, authResultType, error, onSubmit } = props

    const { t } = useTranslation()
    const userDispatch = useUserDispatch()

    const [verificationCode, setVerificationCode] = useState(INITIAL_VERIFICATION_CODE)

    useEffect(() => {
        const code = verificationCode.join('')
        if (code.length === VERIFICATION_CODE_DIGITS) {
            onSubmit(code)
        }
    }, [verificationCode])

    useEffect(() => {
        if (error) {
            setVerificationCode(INITIAL_VERIFICATION_CODE)
        }
    }, [error])

    const showMFAQRCode = authResultType === 'enable_mfa'

    return (
        <Form data-testid="mfa-form" className="flex w-full flex-col items-center text-center" layout={'vertical'}>
            <Title level={2}>{t('Two-factor authentication')}</Title>
            {showMFAQRCode ? (
                <>
                    <Title level={4}>{t('Step 1: Download an authenticator app')}</Title>
                    <Paragraph>
                        {t('Download and install any authenticator app (e.g. Google Authenticator) on your phone.')}
                    </Paragraph>
                    <Title level={4}>{t('Step 2: Authenticate your device')}</Title>
                    <Paragraph>
                        {t(
                            "Open the authenticator app and scan the image below using your phone's camera or enter the secret key.",
                        )}
                    </Paragraph>
                    <div data-testid="qr-code-container">
                        <QRCode className="mb-2" value={uri} />
                    </div>
                    <Paragraph>{t(`Secret key: ${secretKey}`)}</Paragraph>
                    <Title level={4}>{t('Step 3: Enter your code')}</Title>
                    <Paragraph>{t('Enter the 6-digit verification code')}</Paragraph>
                </>
            ) : (
                <Paragraph>
                    {t('Open your authenticator app and enter the generated 6-digit verification code')}
                </Paragraph>
            )}
            <VerificationCodeInput verificationCode={verificationCode} onChange={setVerificationCode} />
            {error ? (
                <Alert
                    message={t('The verification code you provided is invalid. Please try again.')}
                    type="error"
                    data-testid="error-login"
                    className="mt-4"
                />
            ) : null}
            <div className="mt-4 flex w-full justify-center">
                <FormItem>
                    <Button
                        className="mx-2"
                        size={'large'}
                        onClick={() => {
                            removeFromStorage('user')
                            userDispatch({ type: UserActions.CANCEL_LOG_IN })
                        }}
                    >
                        {t('Cancel')}
                    </Button>
                </FormItem>
            </div>
        </Form>
    )
}

export default MFAForm
